// InfiniteCanvasSimple.js - Main class that coordinates all modules
import { CanvasState } from './CanvasState.js';
import { AuthManager } from './AuthManager.js';
import { AIManager } from './AIManager.js';
import { UIManager } from './UIManager.js';
import { InputHandler } from './InputHandler.js';
import { renderCanvas } from './canvasRenderer.js';

export class InfiniteCanvas {
    constructor(canvasId) {
        this.canvas = document.getElementById(canvasId);
        this.ctx = this.canvas.getContext('2d');
        
        // Initialize modules in correct order (AuthManager first)
        this.authManager = new AuthManager();
        this.canvasState = new CanvasState();
        this.uiManager = new UIManager(this.canvasState);
        this.aiManager = new AIManager(this.canvasState, this.uiManager);
        this.inputHandler = new InputHandler(this.canvasState, this.uiManager, this.canvas, this.ctx);

        // Share canvas environment details with the UI layer for focus management
        if (typeof this.uiManager.setCanvasEnvironment === 'function') {
            this.uiManager.setCanvasEnvironment(this.canvas, this.inputHandler);
        }
        
        // Set up cross-references
        this.uiManager.setAuthManager(this.authManager);
        this.uiManager.setAIManager(this.aiManager);
        
        // Initialize the application
        this.setupCanvas();
        this.setupEventListeners();
        this.uiManager.setupUI();
        
        // Load saved data
        this.canvasState.loadFromLocalStorage();
        this.uiManager.initializeChatCanvasState();
        this.canvasState.saveState();
        
        // Start render loop
        this.startRenderLoop();
        
        // Update UI state
        this.uiManager.updateUndoRedoButtons();
        this.uiManager.updateModelSelectorUI();
        
        console.log('🚀 InfiniteCanvas initialized with modular architecture');
    }
    
    setupCanvas() {
        this.resizeCanvas();
        window.addEventListener('resize', () => this.resizeCanvas());
    }
    
    resizeCanvas() {
        const rect = this.canvas.getBoundingClientRect();
        this.canvas.width = rect.width;
        this.canvas.height = rect.height;
    }
    
    setupEventListeners() {
        // UI button events
        document.getElementById('configureBtn').addEventListener('click', () => {
            this.uiManager.showApiKeyModal();
        });
        
        document.getElementById('generateIdeasBtn').addEventListener('click', () => {
            this.aiManager.generateAI();
        });
        
        document.getElementById('enterChatBtn').addEventListener('click', () => {
            this.uiManager.enterChatForSelectedNode();
        });
        
        document.getElementById('generateImageBtn').addEventListener('click', () => {
            this.aiManager.generateImage();
        });
        
        document.getElementById('generateReferenceBtn').addEventListener('click', () => {
            this.aiManager.generateWithReference();
        });
        
        const uploadImageBtn = document.getElementById('uploadImageBtn');
        if (uploadImageBtn) {
            uploadImageBtn.addEventListener('click', () => {
                this.aiManager.uploadImage();
            });
        }
        
        document.getElementById('contextImageBtn').addEventListener('click', () => {
            this.aiManager.contextImage();
        });
        
        document.getElementById('generateVideoBtn').addEventListener('click', () => {
            this.aiManager.generateVideo();
        });
        
        // Canvas control buttons
        document.getElementById('zoomInBtn').addEventListener('click', () => {
            this.inputHandler.zoom(1.2);
        });
        
        document.getElementById('zoomOutBtn').addEventListener('click', () => {
            this.inputHandler.zoom(0.8);
        });
        
        document.getElementById('resetViewBtn').addEventListener('click', () => {
            this.inputHandler.resetView();
        });
        
        document.getElementById('fontSizeIncreaseBtn').addEventListener('click', () => {
            this.inputHandler.increaseFontSize();
        });
        
        document.getElementById('fontSizeDecreaseBtn').addEventListener('click', () => {
            this.inputHandler.decreaseFontSize();
        });
        
        // Undo/Redo buttons
        document.getElementById('undoBtn').addEventListener('click', () => {
            if (this.canvasState.undo()) {
                this.uiManager.updateUndoRedoButtons();
                this.uiManager.showNotification('Undone', 'info', 1000);
            }
        });
        
        document.getElementById('redoBtn').addEventListener('click', () => {
            if (this.canvasState.redo()) {
                this.uiManager.updateUndoRedoButtons();
                this.uiManager.showNotification('Redone', 'info', 1000);
            }
        });
        
        // Import file input
        const importFileInput = document.getElementById('importFileInput');
        if (importFileInput) {
            importFileInput.addEventListener('change', (e) => {
                this.handleFileImport(e);
            });
        }
        
        // Upload image input
        const uploadImageInput = document.getElementById('uploadImageInput');
        if (uploadImageInput) {
            uploadImageInput.addEventListener('change', (e) => {
                this.aiManager.handleImageUpload(e);
            });
        }
    }
    
    handleFileImport(event) {
        const file = event.target.files[0];
        if (!file) return;

        if (!file.type.includes('json') && !file.name.toLowerCase().endsWith('.json')) {
            this.uiManager.showNotification('Please select a valid JSON file', 'error');
            return;
        }

        const maxSize = 10 * 1024 * 1024; // 10MB
        if (file.size > maxSize) {
            this.uiManager.showNotification('File is too large. Maximum size is 10MB', 'error');
            return;
        }

        const reader = new FileReader();
        reader.onload = (e) => {
            try {
                const data = JSON.parse(e.target.result);
                
                if (!data || typeof data !== 'object') {
                    throw new Error('Invalid data format - not an object');
                }

                if (!Array.isArray(data.nodes)) {
                    throw new Error('Invalid or missing nodes data - nodes must be an array');
                }

                if (!Array.isArray(data.connections)) {
                    throw new Error('Invalid or missing connections data - connections must be an array');
                }
                
                // Save current state for undo
                this.canvasState.saveState();
                
                // Load the data
                this.canvasState.nodes = data.nodes || [];
                this.canvasState.connections = data.connections || [];
                
                // Reset view state if provided
                if (typeof data.offsetX === 'number') this.canvasState.offsetX = data.offsetX;
                if (typeof data.offsetY === 'number') this.canvasState.offsetY = data.offsetY;
                if (typeof data.scale === 'number') this.canvasState.scale = data.scale;
                if (typeof data.globalFontSize === 'number') this.canvasState.globalFontSize = data.globalFontSize;
                
                // Validate and fix data
                this.validateAndFixNodes();
                this.validateAndFixConnections();
                
                this.canvasState.clearSelection();
                this.canvasState.rebuildSpatialGrid();
                this.canvasState.saveToLocalStorage();
                this.uiManager.updateUndoRedoButtons();
                
                const nodeCount = this.canvasState.nodes.length;
                const connectionCount = this.canvasState.connections.length;
                this.uiManager.showNotification(
                    `Canvas imported successfully! Loaded ${nodeCount} nodes and ${connectionCount} connections.`,
                    'success'
                );
            } catch (error) {
                console.error('Import error:', error);
                this.uiManager.showNotification('Import failed: ' + error.message, 'error');
            }
        };
        
        reader.onerror = () => {
            this.uiManager.showNotification('Error reading file', 'error');
        };
        
        reader.readAsText(file);
        event.target.value = '';
    }
    
    validateAndFixNodes() {
        this.canvasState.nodes = this.canvasState.nodes.filter(node => {
            if (!node.id || typeof node.text !== 'string') {
                console.warn('Removing invalid node:', node);
                return false;
            }

            node.x = typeof node.x === 'number' ? node.x : 0;
            node.y = typeof node.y === 'number' ? node.y : 0;
            node.width = typeof node.width === 'number' ? node.width : 360;
            node.height = typeof node.height === 'number' ? node.height : 140;
            node.fontSize = typeof node.fontSize === 'number' ? node.fontSize : this.canvasState.globalFontSize;
            node.isSelected = false;

            return true;
        });

        this.canvasState.selectedNodes = [];
    }

    validateAndFixConnections() {
        const validNodeIds = new Set(this.canvasState.nodes.map(node => node.id));

        this.canvasState.connections = this.canvasState.connections.filter(connection => {
            if (!connection.fromNode || !connection.toNode) {
                console.warn('Removing connection with missing fromNode/toNode:', connection);
                return false;
            }

            if (!validNodeIds.has(connection.fromNode) || !validNodeIds.has(connection.toNode)) {
                console.warn('Removing connection with invalid node references:', connection);
                return false;
            }

            return true;
        });
    }
    
    startRenderLoop() {
        const render = () => {
            renderCanvas(
                this.ctx, 
                this.canvas, 
                this.canvasState, 
                this.inputHandler,
                this.uiManager
            );
            requestAnimationFrame(render);
        };
        render();
    }
    
    // Public API methods for external use
    createNode(text, x, y) {
        return this.canvasState.createNode(text, x, y);
    }
    
    deleteNode(node) {
        this.canvasState.deleteSelectedNodes();
    }
    
    selectNode(node) {
        this.canvasState.selectNodes(node);
    }
    
    clearSelection() {
        this.canvasState.clearSelection();
    }
    
    exportData() {
        return {
            nodes: this.canvasState.nodes,
            connections: this.canvasState.connections,
            exportDate: new Date().toISOString()
        };
    }
    
    importData(data) {
        if (data.nodes && Array.isArray(data.nodes)) {
            this.canvasState.saveState();
            this.canvasState.nodes = data.nodes;
            this.canvasState.connections = data.connections || [];
            this.canvasState.clearSelection();
            this.canvasState.rebuildSpatialGrid();
            this.canvasState.saveToLocalStorage();
            return true;
        }
        return false;
    }
} 
